import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
// Modules
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule, NGXS_PLUGINS } from '@ngxs/store';
import { NotificationsService } from 'angular2-notifications';
import { Angulartics2Module } from 'angulartics2';
import { ModalModule } from 'ng2-bootstrap/components/modal/modal.module';
import { ComponentsHelper } from 'ng2-bootstrap/components/utils/components-helper.service';
// Application wide providers
import { FacebookService } from 'ng2-facebook-sdk';
import { ClipboardModule } from 'ngx-clipboard';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { environment } from 'src/environments/environment';
// Components
import { AppComponent } from './app.component';
import {
  ApplicationRoutingModule,
  applicationRoutingProviders
} from './app.routes';
import { logoutPlugin } from './external/views/logout/logout.plugin';
import { FunctionalityService } from './internal/shared/functionality.service';
import { GzNotificationsService } from './internal/shared/gz-notifications.service';
import { ModalService } from './internal/shared/modals/shared/modal/modal.service';
import { MunicipalityCodeSelectionService } from './internal/shared/municipality-code-selection.service';
import { SidebarService } from './internal/shared/sidebar/shared/sidebar.service';
import { ToolSidebarService } from './internal/shared/tool-sidebar/tool-sidebar.service';
import { UserService } from './internal/shared/user.service';
import { DashboardService } from './internal/views/dashboard/dashboard.service';
import { ChoiceService } from './internal/views/survey/services/choice.service';
import { EmailListService } from './internal/views/survey/services/email-list.service';
import { FileService } from './internal/views/survey/services/file.service';
import { NavigationRuleService } from './internal/views/survey/services/navigation-rule.service';
import { PageService } from './internal/views/survey/services/page.service';
import { QuestionService } from './internal/views/survey/services/question.service';
import { SurveyDistributionService } from './internal/views/survey/services/survey-distribution.service';
import { SurveyLinkService } from './internal/views/survey/services/survey-link.service';
import { SurveyResultsService } from './internal/views/survey/services/survey-results.service';
import { SurveyUserService } from './internal/views/survey/services/survey-user.service';
import { SurveyService } from './internal/views/survey/services/survey.service';
import { ApiAvailableDatesService } from './shared/api-available-dates.service';
import { ApiClientService } from './shared/api-client.service';
import { AppState } from './shared/app.state';
import { AuthState } from './shared/auth.state';
import { ErrorTranslatorService } from './shared/error-translator.service';
import { FeatureFlipperService } from './shared/feature-flipper.service';
import { SharedModule } from './shared/shared.module';
// services
import { SurveyAuthService } from './shared/survey-auth.service';
import { TitleService } from './shared/title.service';

// Application wide providers
const providers = [
  {
    provide: NGXS_PLUGINS,
    useValue: logoutPlugin,
    multi: true
  },
  applicationRoutingProviders,
  FeatureFlipperService,
  NotificationsService,
  GzNotificationsService,
  ModalService,
  SidebarService,
  TitleService,
  UserService,
  ApiClientService,
  FunctionalityService,
  ComponentsHelper,
  MunicipalityCodeSelectionService,
  ApiAvailableDatesService,
  FacebookService,
  DashboardService,
  ToolSidebarService,
  TranslateService,
  ErrorTranslatorService,

  // Survey tool specific
  SurveyAuthService,
  SurveyUserService,
  SurveyService,
  PageService,
  QuestionService,
  ChoiceService,
  SurveyLinkService,
  SurveyDistributionService,
  EmailListService,
  FileService,
  NavigationRuleService,
  SurveyResultsService
];

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '../assets/I18n/');
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    // Other root modules
    NgxsModule.forRoot([AuthState, AppState], {
      developmentMode: !environment.production
    }),
    NgxsRouterPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({
      key: [
        'app.language',
        'auth.token',
        'auth.surveyToken',
        'munCodeSel.selectedMunicipalityCode',
        'sidebar.collapsed',
        'view'
      ]
    }),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsLoggerPluginModule.forRoot({
      disabled: environment.production || !environment.showNgxsLog,
      collapsed: true
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    ClipboardModule,
    ModalModule,
    Angulartics2Module.forRoot(),
    LeafletModule.forRoot(),

    // Survey tool specific
    NgxPageScrollCoreModule,

    // Shared Modules
    SharedModule,
    // Own routes always last!
    ApplicationRoutingModule
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: providers
})
export class AppModule {}
